import operateContentInternal from './ContentOperator';
import createTempHtmlEditor from './createTempHtmlEditor';
import setPlainTextEditorSelection from '../actions/setPlainTextEditorSelection';
import updateContent from '../actions/updateContent';
import setCachedSelection from '../actions/setCachedSelection';
import { CompositeEditorViewState } from '../store/schema/CompositeEditorViewState';
import EditorViewState from '../store/schema/EditorViewState';
import * as formatApi from 'roosterjs-editor-api';
import { Editor } from 'roosterjs-editor-core';
import * as domApi from 'roosterjs-editor-dom';

interface HtmlEditorCallbackResponse {
    selectionRange: Range;
    changeSource?: string;
}

interface PlainTextEditorCallbackResponse {
    value: string;
    selectionStart: number;
    selectionEnd: number;
    focus?: boolean;
}

export type OperateContentType = typeof operateContent;

export default function operateContent(
    viewState: EditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi,
        formatUtils: typeof formatApi
    ) => HtmlEditorCallbackResponse
);

export default function operateContent(
    viewState: CompositeEditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi,
        formatUtils: typeof formatApi
    ) => HtmlEditorCallbackResponse,
    plainTextCallback: (
        value: string,
        selectionStart: number,
        selectionEnd: number
    ) => PlainTextEditorCallbackResponse
);

export default function operateContent(
    viewState: EditorViewState,
    htmlCallback: (
        htmlEditor: Editor,
        originalRange: Range,
        domUtils: typeof domApi,
        formatUtils: typeof formatApi
    ) => HtmlEditorCallbackResponse,
    plainTextCallback?: (
        value: string,
        selectionStart: number,
        selectionEnd: number
    ) => PlainTextEditorCallbackResponse
) {
    let content: string;

    operateContentInternal(
        viewState,
        htmlCallback &&
            (htmlEditor => {
                if (htmlEditor) {
                    // CASE 1: HTML, editor already initialized
                    // Directly operate on editor to add undo snapshots (before and after callback),
                    // update content, set selection range, and trigger content changed event

                    htmlEditor.addUndoSnapshot(() => {
                        if (viewState.selectionPath) {
                            htmlEditor.select(viewState.selectionPath);
                            setCachedSelection(viewState, null /*selectionPath*/);
                        }

                        const response = htmlCallback(
                            htmlEditor,
                            htmlEditor.getSelectionRange(),
                            domApi,
                            formatApi
                        );
                        if (response) {
                            const { selectionRange, changeSource } = response;
                            if (selectionRange) {
                                htmlEditor.select(selectionRange);
                            }
                            if (changeSource) {
                                htmlEditor.triggerContentChangedEvent(changeSource);
                            }
                        }
                        content = htmlEditor.getContent();
                    });
                } else {
                    // CASE 2: HTML, editor not yet initialized
                    // Create a temporary editor to operate on and save its response to the view state
                    // so the real editor will have the updated content, selection range, and undo
                    // snapshots (before and after the callback)
                    const tempEditor = createTempHtmlEditor(viewState);
                    tempEditor.select(viewState.selectionPath);

                    tempEditor.addUndoSnapshot(() => {
                        const response = htmlCallback(
                            tempEditor,
                            tempEditor.getSelectionRange(),
                            domApi,
                            formatApi
                        );
                        content = tempEditor.getContent();
                        tempEditor.select(response?.selectionRange);
                    });

                    setCachedSelection(viewState, tempEditor.getSelectionPath());

                    tempEditor.dispose();
                }
            }),
        plainTextCallback &&
            (plainTextEditor => {
                if (plainTextEditor) {
                    // CASE 3: Plain Text, editor already initialized
                    // Directly set the value and selection of the response to the editor

                    const response = plainTextCallback(
                        plainTextEditor.value,
                        plainTextEditor.selectionStart,
                        plainTextEditor.selectionEnd
                    );
                    if (response) {
                        const { value, selectionStart, selectionEnd, focus } = response;
                        content = plainTextEditor.value = value;
                        if (focus) {
                            plainTextEditor.focus();
                            const newSelectionStart = selectionStart || 0;
                            const newSelectionEnd = selectionEnd || 0;
                            plainTextEditor.selectionStart = newSelectionStart;
                            plainTextEditor.selectionEnd = newSelectionEnd;
                            setPlainTextEditorSelection(
                                viewState,
                                newSelectionStart,
                                newSelectionEnd
                            );
                        }
                    }
                } else {
                    // CASE 4: Plain Text, editor not yet initialized
                    // Save the response to the view state so that when the editor is created
                    // and mounted it will have the correct content and selection

                    const response = plainTextCallback(
                        viewState.content /* value */,
                        viewState.selectionStart || 0 /* selectionStart */,
                        viewState.selectionEnd || 0 /* selectionEnd */
                    );
                    if (response) {
                        const { value, selectionStart, selectionEnd } = response;
                        content = value;
                        setPlainTextEditorSelection(
                            viewState,
                            selectionStart || 0,
                            selectionEnd || 0
                        );
                    }
                }
            }),
        true /*alwaysInvokeCallback*/
    );

    // In all cases, save operated content to the view state
    if (content != null) {
        updateContent(viewState, content);
    }
}
